import type { WorkTaskAutomation, WorkTaskAutomationInstructionStep } from '../../../data-contract/task-management';

// The recipe is generated in English and used as an agent definition, so its labels stay English.
const TRIGGER_LABELS = { event: 'Event', manual: 'Manual', schedule: 'Schedule' } as const;
const ACTOR_LABELS = { ai: 'AI', human: 'Human', system: 'System' } as const;

const hasText = (value: string | undefined): value is string => value !== undefined && value.trim().length > 0;

const stepLine = (step: WorkTaskAutomationInstructionStep, index: number): string => {
    const title = hasText(step.title) ? `**${step.title.trim()}**` : undefined;
    const actor = step.actor === undefined ? undefined : `(${ACTOR_LABELS[step.actor]})`;
    const head = [title, actor].filter(hasText).join(' ');
    const description = step.description?.trim();

    return `${index + 1}. ${[head, description].filter(hasText).join(': ')}`;
};

const stepsSection = (heading: string, steps: readonly WorkTaskAutomationInstructionStep[]): string | undefined =>
    steps.length === 0 ? undefined : [`## ${heading}`, ...steps.map(stepLine)].join('\n');

/**
 * Assembles an automation's build plan into one markdown document, shaped like an agent or skill
 * definition. Empty sections are left out; steps without a phase follow under a plain "Steps".
 */
export const toAutomationRecipeMarkdown = (automation: Pick<WorkTaskAutomation, 'instructions' | 'title'>): string => {
    const { brief, constraints = [], steps = [], trigger } = automation.instructions ?? {};
    const triggerLine = [
        trigger?.kind === undefined ? undefined : TRIGGER_LABELS[trigger.kind],
        trigger?.description?.trim(),
    ]
        .filter(hasText)
        .join(': ');

    return [
        hasText(automation.title) ? `# ${automation.title.trim()}` : undefined,
        brief?.trim(),
        triggerLine.length === 0 ? undefined : `## Trigger\n${triggerLine}`,
        stepsSection(
            'Setup',
            steps.filter((step) => step.phase === 'setup'),
        ),
        stepsSection(
            'Run',
            steps.filter((step) => step.phase === 'run'),
        ),
        stepsSection(
            'Steps',
            steps.filter((step) => step.phase === undefined),
        ),
        constraints.length === 0
            ? undefined
            : ['## Constraints', ...constraints.map((constraint) => `- ${constraint.trim()}`)].join('\n'),
    ]
        .filter(hasText)
        .join('\n\n');
};

export const toAutomationRecipeFileName = (title: string | undefined): string => {
    const slug = (title ?? '')
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, '-')
        .replaceAll(/^-+|-+$/g, '');

    return `${slug.length === 0 ? 'automation' : slug}.md`;
};

/**
 * Hands the recipe to the browser as a markdown file named `fileName`. The object URL is revoked on
 * the next task, once the click has started the download.
 */
export const downloadAutomationRecipe = ({ fileName, markdown }: { fileName: string; markdown: string }): void => {
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const RECIPE_VIEWS = ['source', 'preview'] as const;

export type RecipeView = (typeof RECIPE_VIEWS)[number];

/** The source is the artifact people copy into an agent setup, so it is what the file shows first. */
export const DEFAULT_RECIPE_VIEW: RecipeView = 'source';

export const toRecipeView = (value: string): RecipeView =>
    RECIPE_VIEWS.find((view) => view === value) ?? DEFAULT_RECIPE_VIEW;
